// src/pages/ProfilePage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { 
  FiUser, 
  FiKey, 
  FiFileText, 
  FiLoader, 
  FiEye, 
  FiEyeOff,
  FiBriefcase
} from 'react-icons/fi';

const ProfilePage = () => {
  const { session, userProfile, refreshUserProfile } = useOutletContext();
  
  // État pour les données du formulaire
  const [profileData, setProfileData] = useState({
    email: '',
    person_full_name: '',
    phone: '',
    company_name: '',
    signer_full_name: '',
    signer_title: '',
    address: '',
    postal_code: '',
    city: '',
    siren: '',
  });

  const [initialProfileData, setInitialProfileData] = useState({});
  const [activeTab, setActiveTab] = useState("informations");
  
  // États pour la gestion du mot de passe
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  // États pour les messages et le chargement
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);

  // Remplissage initial du formulaire à partir des données du contexte
  useEffect(() => {
    if (userProfile && session) {
      const data = {
        email: session.user.email || '',
        person_full_name: userProfile.person_full_name || '',
        phone: userProfile.phone || '',
        company_name: userProfile.company_name || '',
        signer_full_name: userProfile.signer_full_name || '',
        signer_title: userProfile.signer_title || '',
        address: userProfile.address || '',
        postal_code: userProfile.postal_code || '',
        city: userProfile.city || '',
        siren: userProfile.siren || '',
      };
      setProfileData(data);
      setInitialProfileData(data); // Garde une copie pour comparer les changements
    }
  }, [userProfile, session]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setMessage({ type: '', text: '' });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!userProfile) return;
    
    // On compare les données actuelles avec les données initiales
    if (JSON.stringify(profileData) === JSON.stringify(initialProfileData)) {
      setMessage({ type: "info", text: "Aucune modification détectée." });
      return;
    }

    setMessage({ type: "", text: "" });
    setIsSubmittingProfile(true);

    try {
      // On prépare les données à mettre à jour (sans l'email qui ne doit pas changer)
      const { email, ...updateData } = profileData;

      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", userProfile.id);

      if (error) throw error;
      
      setMessage({ type: "success", text: "Profil mis à jour avec succès !" });
      await refreshUserProfile(); // On rafraîchit les données dans toute l'app
      
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil:", error);
      setMessage({ type: "error", text: `Erreur: ${error.message}` });
    } finally {
      setIsSubmittingProfile(false);
    }
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    // ... (logique inchangée pour le mot de passe)
  };

  const tabs = [
    { id: "informations", label: "Contact", icon: <FiUser className="mr-2" /> },
    { id: "entreprise", label: "Entreprise", icon: <FiBriefcase className="mr-2" /> },
    { id: "securite", label: "Sécurité", icon: <FiKey className="mr-2" /> },
  ];

  const inputBaseClasses = "mt-1 block w-full rounded-md border-slate-300 p-2.5 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-slate-50 disabled:text-slate-500";
  const labelBaseClasses = "block text-sm font-medium text-slate-700";

  if (!userProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <FiLoader className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  return (
          <div className="mx-auto max-w-3xl">
            <h1 className="mb-6 text-2xl font-semibold text-slate-800 md:text-3xl">Paramètres du compte</h1>

            {/* Onglets de navigation */}
            <div className="mb-6 border-b border-slate-300">
              <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMessage({type:'', text:''}); setPasswordMessage({type:'', text:''});}}
                    className={`flex items-center whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors
                      ${activeTab === tab.id
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                      }`}
                  >
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Contenu des onglets */}
            <div className="rounded-lg bg-white p-6 shadow-lg sm:p-8">
              {activeTab === "informations" && (
  <form onSubmit={handleProfileSubmit} className="space-y-6">
    <h2 className="text-xl font-semibold text-slate-700">Informations de l'entreprise et du contact</h2>
    {message.text && <p className={`text-sm p-3 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{message.text}</p>}

    {/* ✅ La grille contenant tous les champs */}
    <div className="grid grid-cols-1 gap-y-5 gap-x-6 sm:grid-cols-2">
      {/* Raison sociale */}
      <div>
        <label htmlFor="companyName" className={labelBaseClasses}>Raison sociale</label>
        <input type="text" name="companyName" value={profileData.companyName} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>
      
      {/* Email */}
      <div>
        <label htmlFor="email" className={labelBaseClasses}>Email de contact</label>
        <input type="email" name="email" value={profileData.email} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>

      {/* Nom représentant */}
      <div>
        <label htmlFor="signerFullName" className={labelBaseClasses}>Nom du représentant légal</label>
        <input type="text" name="signerFullName" value={profileData.signerFullName} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>

      {/* Qualité représentant */}
      <div>
        <label htmlFor="signerTitle" className={labelBaseClasses}>Qualité du représentant</label>
        <input type="text" name="signerTitle" value={profileData.signerTitle} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>

      {/* Adresse */}
      <div className="sm:col-span-2">
        <label htmlFor="address" className={labelBaseClasses}>Adresse du siège social</label>
        <input type="text" name="address" value={profileData.address} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>

      {/* Code Postal */}
      <div>
        <label htmlFor="postalCode" className={labelBaseClasses}>Code Postal</label>
        <input type="text" name="postalCode" value={profileData.postalCode} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>

      {/* Ville */}
      <div>
        <label htmlFor="city" className={labelBaseClasses}>Ville</label>
        <input type="text" name="city" value={profileData.city} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>
      
      {/* SIREN */}
      <div>
        <label htmlFor="siren" className={labelBaseClasses}>N° SIREN</label> {/* ✅ Texte corrigé */}
        <input type="text" name="siren" value={profileData.siren} readOnly disabled className={`${inputBaseClasses} cursor-not-allowed`} />
      </div>
      
      {/* Téléphone */}
      <div>
        <label htmlFor="phone" className={labelBaseClasses}>Téléphone</label>
        <input 
          type="tel" 
          name="phone" 
          id="phone" 
          value={profileData.phone} 
          onChange={handleChange}
          className={inputBaseClasses}
          disabled={isSubmittingProfile} 
        />
      </div>
    </div> {/* <-- ✅ Fin de la grille */}

    {/* ✅ Le bouton est dans son propre conteneur, APRÈS la grille */}
    <div className="mt-6 border-t border-slate-200 pt-5 text-right">
      <button type="submit" disabled={isSubmittingProfile} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50">
        {isSubmittingProfile ? "Enregistrement..." : "Enregistrer les modifications"}
      </button>
    </div>
  </form>
)}

              {activeTab === "securite" && (
                <form onSubmit={handlePasswordChangeSubmit} className="space-y-6">
                  <h2 className="text-xl font-semibold text-slate-700">Changer le mot de passe</h2>
                  {passwordMessage.text && <p className={`text-sm p-3 rounded-md ${passwordMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{passwordMessage.text}</p>}
                  
                  <div>
                    <label htmlFor="newPassword" className={labelBaseClasses}>Nouveau mot de passe *</label>
                    <div className="relative mt-1">
                      <input type={showNewPassword ? "text" : "password"} name="newPassword" id="newPassword" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className={`${inputBaseClasses} pr-10`} disabled={isPasswordSubmitting} />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700">
                        {showNewPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className={labelBaseClasses}>Confirmer le nouveau mot de passe *</label>
                     <div className="relative mt-1">
                      <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={`${inputBaseClasses} pr-10`} disabled={isPasswordSubmitting} />
                       <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700">
                        {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                  <div className="pt-3 text-right">
                     <button type="submit" disabled={isPasswordSubmitting} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50">
                      {isPasswordSubmitting ? "Modification..." : "Changer le mot de passe"}
                    </button>
                  </div>
                </form>
              )}
{/* NOUVEL ONGLET: Mon Contrat */}
              {activeTab === "mon-contrat" && (
          <SignedContractSection userId={user?.id} />
        )}
            </div>
          </div>
  );
};
export default ProfilePage;